import { Alert, Button, Form } from 'antd';
import { ShieldCheck } from 'lucide-react';
import { CodeInput } from '../../../shared/components/CodeInput';
import { DisplayedCodeNotice } from '../../../shared/components/DisplayedCodeNotice';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import type { Confirmation } from '../hooks/use-confirmation';
import { confirmationDestination } from '../policies/confirmation';

/** Shown in place of the action it guards while `confirmation` waits for the code; renders nothing otherwise. */
export function ConfirmItsYouPanel({ confirmation }: { confirmation: Confirmation }) {
  const { state: session } = useCurrentUser();
  const { state, error, submitting, confirm, resend, cancel } = confirmation;
  if (state.phase !== 'code' || session.status !== 'authenticated') return null;

  const destination = confirmationDestination(state.sent.channel, session.user);

  return (
    <section aria-label="Confirm it's you" className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-5">
      <div className="flex items-center gap-2">
        <ShieldCheck className="size-4 text-primary" />
        <h3 className="text-sm font-semibold text-fg">Confirm it's you</h3>
      </div>
      <p className="mt-1.5 text-sm text-muted">
        To {state.purpose}, enter the code we just sent {destination}.
      </p>
      {state.sent.displayedCode && <DisplayedCodeNotice className="mt-4" code={state.sent.displayedCode} />}
      {error && <Alert className="mt-4" type="error" title={error} showIcon />}
      <Form<{ code: string }> className="mt-4" layout="vertical" requiredMark={false} onFinish={(values) => confirm(values.code)} disabled={submitting}>
        <Form.Item name="code" label="Enter the 6-digit code" rules={[{ required: true, message: 'Enter the 6-digit code' }]}>
          <CodeInput autoFocus onComplete={confirm} />
        </Form.Item>
        <div className="flex flex-wrap gap-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Confirm
          </Button>
          <Button onClick={cancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="link" onClick={resend} disabled={submitting}>
            Send another code
          </Button>
        </div>
      </Form>
    </section>
  );
}
